import React, { useEffect, useState } from 'react'
import { Modal, ModalContent } from 'shared/components/common/modal'
import { t } from 'src/translations'
import { useFormatPriceCallback } from 'src/lib/js/hooks/useFormatPrice'
import { CompletedOrder } from 'src/public/components/online_ordering/types'
import { GuestRefundMethod, RefundMethod } from '../lib/dataMapper'
import { refundTotalAmount } from '../lib/refundItemPricing'
import { InitializedRefund, RefundQuote } from '../lib/useInitializeRefund'
import RecoveryModalHeader from './RecoveryModalHeader'
import RecoveryModalFooter from './RecoveryModalFooter'
import RecoveryChoiceRow from './RecoveryChoiceRow'
import useRovingRadioGroup from './useRovingRadioGroup'

type Method = GuestRefundMethod

// Render order of the two rows, so the arrow keys move between them.
const METHODS: readonly Method[] = [
  RefundMethod.ToastCash,
  RefundMethod.OriginalPaymentMethod
]

interface Props {
  isOpen: boolean
  onClose: () => void
  // Back to the previous recovery step (D1/D2). Shows the header back chevron.
  onBack?: () => void
  order: CompletedOrder
  // What guestCurrency reserved for this claim — the only source of truth for the number
  // (order.totalV2 is tax-inclusive and was never reserved). The whole quote, not just the amounts,
  // because this is the first screen after the mutation: pending and declined are states it renders.
  quote: RefundQuote
  // Re-ask for a quote. Only reachable when quote.retryable.
  onRetry: () => void
  // The value is the exact Intercom 'Selected refund method' the workflow routes on.
  onSelectMethod: (method: Method) => void
}

// D3: two recovery paths as radio rows + one CTA, disabled until a row is chosen.
export const RefundResolutionModal = ({
  isOpen,
  onClose,
  onBack,
  order,
  quote,
  onRetry,
  onSelectMethod
}: Props) => {
  // Nothing selected by default; the CTA stays disabled until the guest picks (design §1).
  const [selected, setSelected] = useState<Method | null>(null)

  // Reset on each open so a prior selection never carries into a new recovery session.
  useEffect(() => {
    if (isOpen) setSelected(null)
  }, [isOpen])

  // Still-waiting when the mutation has neither answer nor verdict, not just while `loading`: the
  // card fires the quote in the handler that opens this, so there is a beat before Apollo reports
  // loading, and defaulting it to the failure state would flash an error on every open.
  const pending = quote.loading || (!quote.refund && !quote.settled)
  // Hidden while re-quoting, so a retry never shows the previous attempt's amount as the new one.
  const refund = pending ? null : quote.refund

  // Toast Cash = base + bonus; original payment = base only (design §1). useFormatPriceCallback, not
  // useFormatMoney: only it mirrors the AUD narrowSymbol case and reads the restaurant's i18n — the
  // currency the service quoted in, so D2's prices and these amounts always agree.
  const formatPrice = useFormatPriceCallback()

  // Masked card label for the Original-payment destination (e.g. "Visa ···· 4242").
  const payment = order.payments?.[0]
  const cardLabel =
    payment?.last4Digits &&
    `${
      payment.cardType && payment.cardType !== 'UNKNOWN' ? payment.cardType : ''
    } ···· ${payment.last4Digits}`.trim()

  // CTA label reflects the selection + amount + destination (design §1). Takes the refund rather
  // than closing over it, so the amounts are only ever read where one exists.
  const ctaLabel = (quoted: InitializedRefund): string => {
    if (selected === RefundMethod.ToastCash) {
      return t('order-confirmation.refund-resolution.cta.toast-cash', {
        amount: formatPrice(refundTotalAmount(quoted))
      })
    }
    if (selected !== RefundMethod.OriginalPaymentMethod) {
      return t('order-confirmation.refund-resolution.select-prompt')
    }
    const baseAmountLabel = formatPrice(Number(quoted.baseAmount.amount))
    return cardLabel
      ? t('order-confirmation.refund-resolution.cta.original-payment', {
          amount: baseAmountLabel,
          destination: cardLabel
        })
      : t(
          'order-confirmation.refund-resolution.cta.original-payment-fallback',
          { amount: baseAmountLabel }
        )
  }

  const confirm = () => {
    if (selected) onSelectMethod(selected)
  }

  // Single tab stop + arrow keys over the two rows, as a radio group is expected to behave.
  const rowProps = useRovingRadioGroup(METHODS, selected, (method: Method) =>
    setSelected(method)
  )

  // Carries all three quote states, so a swap lands where the screen reader already points.
  const subtitle = (): string => {
    if (pending) return t('order-confirmation.refund-resolution.quote-pending')
    if (!refund) return t('order-confirmation.refund-resolution.quote-error')
    return t('order-confirmation.refund-resolution.subtitle')
  }

  const renderOptions = (quoted: InitializedRefund) => (
    <div
      role='radiogroup'
      aria-labelledby='refund-resolution-title'
      className='resolutionOptions'
    >
      <RecoveryChoiceRow
        role='radio'
        isSelected={selected === RefundMethod.ToastCash}
        className='recoveryChoice toastCash'
        testId='refund-resolution-toast-cash'
        {...rowProps(RefundMethod.ToastCash, 0)}
      >
        <span className='optionTitle'>
          {t('order-confirmation.refund-resolution.toast-cash.amount', {
            amount: formatPrice(refundTotalAmount(quoted))
          })}
        </span>
        {/* Badge + detail. The bonus is whatever guestCurrency quoted, not a fixed kicker. */}
        <span className='detail'>
          <span className='availableNow'>
            {t('order-confirmation.refund-resolution.toast-cash.badge')}
          </span>
          {' · '}
          {t('order-confirmation.refund-resolution.toast-cash.detail', {
            bonus: formatPrice(Number(quoted.bonusAmount.amount))
          })}
        </span>
      </RecoveryChoiceRow>

      <RecoveryChoiceRow
        role='radio'
        isSelected={selected === RefundMethod.OriginalPaymentMethod}
        className='recoveryChoice originalPayment'
        testId='refund-resolution-original-payment'
        {...rowProps(RefundMethod.OriginalPaymentMethod, 1)}
      >
        <span className='optionTitle'>
          {cardLabel
            ? t(
                'order-confirmation.refund-resolution.original-payment.title-card',
                {
                  amount: formatPrice(Number(quoted.baseAmount.amount)),
                  destination: cardLabel
                }
              )
            : t('order-confirmation.refund-resolution.original-payment.title', {
                amount: formatPrice(Number(quoted.baseAmount.amount))
              })}
        </span>
        <span className='detail'>
          {t('order-confirmation.refund-resolution.original-payment.detail')}
        </span>
      </RecoveryChoiceRow>
    </div>
  )

  // One button in every state except a declined claim: with BAD_REQUEST or DENIED there is nothing
  // to press and nothing to retry, so the guest leaves via the header's back or close.
  const renderFooterAction = () => {
    if (pending) {
      // Disabled rather than omitted, so the footer doesn't appear from nowhere when the quote
      // lands, shifting the body under the guest's cursor.
      return (
        <button
          type='button'
          className='resolutionCta'
          data-testid='refund-resolution-confirm'
          disabled
        >
          {t('order-confirmation.refund-resolution.select-prompt')}
        </button>
      )
    }
    if (refund) {
      /* Disabled until a path is chosen; label reflects the selection + amount (design §1). */
      return (
        <button
          type='button'
          className='resolutionCta'
          data-testid='refund-resolution-confirm'
          disabled={!selected}
          onClick={confirm}
        >
          {ctaLabel(refund)}
        </button>
      )
    }
    if (quote.retryable) {
      return (
        <button
          type='button'
          className='resolutionCta'
          data-testid='refund-resolution-retry'
          onClick={onRetry}
        >
          {t('order-confirmation.refund-resolution.quote-retry')}
        </button>
      )
    }
    return null
  }

  const footerAction = renderFooterAction()

  return (
    // No dark overlay, and positioned where Intercom's Messenger natively renders, so confirming
    // hands off to the panel in the SAME place and size — one continuous surface to the guest.
    // See refund_resolution_modal.scss.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId='refund-resolution-modal'
      className='refundSupportModal'
    >
      <ModalContent
        wrapperClassName='refundSupportWrapper'
        contentClassName='refundResolutionModal'
        ariaLabelledBy='refund-resolution-title'
        ariaDescribedBy='refund-resolution-subtitle'
        // The focus-trap activates on a 500ms delay; confirming closes this and opens the
        // Messenger, so the trap would fire after unmount and steal focus back, closing its panel.
        disableFocusTrap
      >
        <RecoveryModalHeader onBack={onBack} />
        <div className='refundResolutionBody'>
          <h2 id='refund-resolution-title' className='title' tabIndex={-1}>
            {t('order-confirmation.refund-resolution.title')}
          </h2>
          {/* aria-live: this line is where waiting on the quote, and failing, are reported. */}
          <p
            id='refund-resolution-subtitle'
            className='subtitle'
            aria-live='polite'
          >
            {subtitle()}
          </p>

          {refund && renderOptions(refund)}

          {/* 14-day claims notice. */}
          <p className='recoveryFinePrint'>
            {t('order-confirmation.refund-resolution.claims-notice')}
          </p>
        </div>
        {footerAction && (
          <RecoveryModalFooter>{footerAction}</RecoveryModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}

export default RefundResolutionModal
